import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";

const ARTICLES = [
  {
    title: "Why Consistent Content Builds Brand Trust",
    excerpt: "How rhythm and consistency, not virality, become the long-term currency of brand-led growth.",
    category: "Brand Building",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwZGF0YSUyMGRhc2hib2FyZCUyMGFuYWx5dGljcyUyMGRhcmt8ZW58MHx8fHwxNzgwODU2MTg2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "How Reels Can Strengthen Brand Recall",
    excerpt: "Inside the craft of platform-native short-form video — and the templates that work for premium brands.",
    category: "Content Strategy",
    img: "https://images.unsplash.com/photo-1642945538257-d1e69abfb085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGx1eHVyeSUyMGRhcmslMjBnb2xkJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MDg1NjE4Nnww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Why Performance Marketing Needs Better Creative",
    excerpt: "Targeting has been commoditised. Creative is the last real lever — here's how to think about it.",
    category: "Performance",
    img: "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Insights() {
  useEffect(() => { document.title = "Insights — The Indian Marketing"; }, []);

  return (
    <div data-testid="insights-page" className="bg-[#2E2E31] text-white">
      <section className="pt-40 pb-16 md:pt-48 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">The Studio Journal</span>
          </div>
          <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05] max-w-4xl">
            Ideas, insights and <em className="text-[#D4AF37] font-normal">growth strategies</em>.
          </h1>
          <p className="font-outfit text-lg md:text-xl text-[#D9D9D9] mt-10 max-w-3xl leading-relaxed">
            Long-form perspectives on content, brand, performance marketing and growth. New essays drop monthly — sign up to be the first to read.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.map((a, i) => (
              <motion.article
                key={i}
                data-testid={`insight-card-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group bg-[#1c1c1f] border border-white/10 hover:border-[#D4AF37]/60 transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden relative zoom-on-hover">
                  <img src={a.img} alt={a.title} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 bg-[#111111]/85 backdrop-blur-md text-[#D4AF37] font-outfit text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-[#D4AF37]/30">
                    {a.category}
                  </div>
                  <div className="absolute bottom-4 right-4 bg-[#D4AF37] text-black font-outfit text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 flex items-center gap-1.5">
                    <Clock size={11} /> Coming Soon
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="font-playfair text-2xl text-white tracking-tight leading-tight mb-4 group-hover:text-[#D4AF37] transition-colors">
                    {a.title}
                  </h3>
                  <p className="font-outfit text-sm text-[#D9D9D9] leading-relaxed flex-1">{a.excerpt}</p>
                  <div className="mt-8 inline-flex items-center gap-2 text-[#D4AF37]/60 font-outfit text-xs uppercase tracking-[0.2em]">
                    Full essay arriving soon
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-20 text-center">
            <Link
              to="/contact"
              data-testid="insights-cta-notify"
              className="inline-flex items-center gap-3 bg-transparent text-white border border-[#D4AF37] font-outfit font-medium px-8 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37]/10 transition-colors"
            >
              Notify Me When New Essays Drop <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
